import React, { useEffect, useState } from 'react'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { TableCell, TableRow, InputLabel, FormControl, FormLabel, Chip, TableBody, Table, Box, CircularProgress } from '@material-ui/core';
import { RxOrder, RxOrderStatusEnum, Option, OptionId, Status } from 'openapi';
import LensSelectFormik from '../inputs/LensSelectFormik';
import MakerSelectFormik from '../inputs/MakerSelectFormik';
import CommonContainer from '../../containers/Common';
import StatusColumn from '../labels/StatusColumn';
import EditDeleteButtons from '../buttons/EditDeleteButtons';
import RxOrderContainer from '../../containers/RxOrder';
import { Field, Formik, FormikHelpers, Form } from 'formik';
import { TextField } from 'formik-material-ui';
import ColorSelectFormik from '../inputs/ColorSelectFormik';
import ConfirmCancelButtons from '../buttons/ConfirmCancelButtons';
import ColorTypeSelectFormik from '../inputs/ColorTypeSelectFormik';
import DensitySelectFormik from '../inputs/DensitySelectFormik';
import LeftOptionSelectFormik from '../inputs/LeftOptionSelectFormik';
import RightOptionSelectFormik from '../inputs/RightOptionSelectFormik';
import PdOptionText from '../inputs/PdOptionText';
import StatusPanel from '../panels/StatusPanel';

interface Props {
  rowData: RxOrder,
  date: Date,
}

interface FormValues {
  meganetLensRxOrderId: number,
  customerName?: string,
  status: RxOrderStatusEnum,
  recordedDate: Date,
  leftMeganetLensMasterId: number,
  leftSph: number,
  leftCyl: number,
  leftAx: number,
  leftAdd: number,
  leftOutDia: number,
  leftPd: number,
  rightMeganetLensMasterId: number,
  rightSph: number,
  rightCyl: number,
  rightAx: number,
  rightAdd: number,
  rightOutDia: number,
  rightPd: number,
  meganetColorId: number,
  meganetColorTypeId: number,
  meganetDensityId: number,
  remarks?: string,
  canEditRemarks?: boolean,
  leftOptionItemIds?: OptionId[],
  rightOptionItemIds?: OptionId[],
}

const useStyles = makeStyles((theme: Theme) => createStyles({
  table: {
    minWidth: 650,
  },
  formControl: {
    margin: theme.spacing(1),
    display: 'block'
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  tableSpacing: {
    marginRight: theme.spacing(5),
    marginLeft: theme.spacing(5),
  },
}));

export default ((props) => {
  const classes = useStyles({});
  const commonContainer = CommonContainer.useContainer();
  const rxOrderContainer = RxOrderContainer.useContainer();
  const [open, setOpen] = useState(false);
  const [statusList, setStatusList] = useState<Status[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const handleEdit = (meganetLensRxOrderId: number) => {
    rxOrderContainer.setIsEditing(true);
    rxOrderContainer.setSelectedOrderId(meganetLensRxOrderId ? meganetLensRxOrderId : -1);
  };

  const handleCancel = (meganetLensRxOrderId: number | undefined) => {
    if (rxOrderContainer.isEditing) {
      rxOrderContainer.setIsEditing(false);
    }

    if (rxOrderContainer.isDeleting) {
      rxOrderContainer.setIsDeleting(false);
    }
    // Reset selectedOrderId
    rxOrderContainer.setSelectedOrderId(-1);
  }

  const handleDelete = (meganetLensRxOrderId: number) => {
    rxOrderContainer.setIsDeleting(true);
    rxOrderContainer.setSelectedOrderId(meganetLensRxOrderId ? meganetLensRxOrderId : -1);
  };

  const handleSumbit = async (values: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
    (async () => {
      if (rxOrderContainer.isEditing) {
        // Update row
        values.recordedDate = props.date;
        values.meganetLensRxOrderId = rxOrderContainer.selectedOrderId;

        var meganetColorDensityMappingId = rxOrderContainer.getMeganetColorDensityMappingId(values.meganetColorId, values.meganetColorTypeId, values.meganetDensityId);
        var valuesWithMappingId = {
          ...values,
          meganetColorDensityMappingId: meganetColorDensityMappingId,
          rightAdd: commonContainer.getLens(values.rightMeganetLensMasterId)?.canAdd ? values.rightAdd : 0,
          leftAdd: commonContainer.getLens(values.leftMeganetLensMasterId)?.canAdd ? values.leftAdd : 0,
        };

        await rxOrderContainer.update(valuesWithMappingId);
        await rxOrderContainer.load(props.date);
        rxOrderContainer.setIsEditing(false);
      }

      if (rxOrderContainer.isDeleting) {
        values.meganetLensRxOrderId = rxOrderContainer.selectedOrderId;
        // Delete row
        await rxOrderContainer.remove(values.meganetLensRxOrderId);
        rxOrderContainer.setIsDeleting(false);
      }
    })()

    formikHelpers.setSubmitting(false);
  }

  const handleStatus = async (orderId: number) => {
    // Show panel
    setOpen(true);

    // get status List
    var results = await rxOrderContainer.getStatusList(orderId);
    setStatusList(results ? results : []);
  }

  const handleClose = () => {
    setOpen(false);
  };

  var color: any = rxOrderContainer.getColorByColorDensityMappingId(props.rowData.meganetColorDensityMappingId);

  var initialValus: FormValues = {
    ...props.rowData,
    meganetColorId: color ? color.meganetColorId as number : -1,
    meganetColorTypeId: color ? color.meganetColorTypeId as number : -1,
    meganetDensityId: color ? color.meganetDensityId as number : -1,
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true)
      await rxOrderContainer.loadOptions(props.rowData.leftMeganetLensMasterId, 0);
      await rxOrderContainer.loadOptions(props.rowData.rightMeganetLensMasterId, 1);
      setIsLoading(false);
    })();
  }, []);

  const loadOptionsByMeganetLensMasterId = async (meganetLensMasterId: number, leftRight?: number) => {
    setIsLoading(true)
    await rxOrderContainer.loadOptions(meganetLensMasterId, leftRight);
    setIsLoading(false);
  }

  return (
    <>
      <StatusPanel handleClickOpen={() => handleStatus} handleClose={handleClose} open={open} statusList={statusList} />
      <TableRow>
        <TableCell>
          <Formik
            initialValues={initialValus}
            onSubmit={handleSumbit}
          >
            {({ isSubmitting, values, setValues, setFieldValue }) => (
              <Form className={classes.form} noValidate>
                <Table key={props.rowData.meganetLensRxOrderId}>
                  <TableBody>
                    <TableRow >
                      <TableCell><StatusColumn status={props.rowData.status} orderId={props.rowData.meganetLensRxOrderId} handleStatus={handleStatus} /></TableCell>
                      {
                        props.rowData.karteNo ?
                        <TableCell align='right'>
                          <FormLabel id='karteNoLabel' style={{ fontSize: 13, }}>Karte No</FormLabel>
                          <br />
                          <FormLabel id='karteNo' style={{ color: 'black' }}>{props.rowData.karteNo !== undefined ? props.rowData.karteNo : ''}</FormLabel>
                        </TableCell> :
                        <TableCell align='right'><FormControl className={classes.tableSpacing}/></TableCell>
                      }
                      {
                        props.rowData.customerName ? 
                        <TableCell align='right'>
                          <FormLabel id='customerNameLabel' style={{ fontSize: 13, }}>Customer Name</FormLabel>
                          <br />
                          <FormLabel id='customerName' style={{ color: 'black' }}>{props.rowData.customerName}</FormLabel>
                        </TableCell> :
                        <TableCell align='right'><FormControl className={classes.tableSpacing}/></TableCell>
                      }
                      <TableCell align='right'><FormControl className={classes.tableSpacing}/></TableCell>
                      <TableCell align='left'>
                        <FormControl className={classes.formControl}>
                          <InputLabel id='leftMeganetLensMasterId' style={{ fontSize: 13, }}>Maker</InputLabel>
                          <MakerSelectFormik
                            makerCode={(() => {
                              const maker = commonContainer.getMaker(values.leftMeganetLensMasterId);
                              return maker ? maker.makerCode : '';
                            })()}
                            onChange={(makerCode) => {
                              setFieldValue('maker', makerCode);
                              const lenses = commonContainer.getLensesByMakerCode(makerCode);
                              const rxLenses = lenses.find(lens => lens.lensType === 1);
                              setFieldValue('leftMeganetLensMasterId', rxLenses ? rxLenses.meganetLensMasterId : -1)
                              setFieldValue('rightMeganetLensMasterId', rxLenses ? rxLenses.meganetLensMasterId : -1)

                              if (rxLenses) {
                                loadOptionsByMeganetLensMasterId(rxLenses.meganetLensMasterId);
                              }

                              setFieldValue('rightOptionItemIds', []);
                              setFieldValue('leftOptionItemIds', []);
                            }}
                          />
                        </FormControl>
                      </TableCell>
                      <TableCell align='left'>
                        <FormControl className={classes.formControl}>
                          <InputLabel id='color'>Color</InputLabel>
                          <ColorSelectFormik
                            leftMeganetLensMasterId={values.leftMeganetLensMasterId}
                            rightMeganetLensMasterId={values.rightMeganetLensMasterId}
                            meganetColorId={values.meganetColorId}
                            onChange={(meganetColorId) => {
                              if (meganetColorId !== -1) {
                                var colorTypes = rxOrderContainer.getColorTypeByMeganetColorId(meganetColorId);
                                var densities = rxOrderContainer.getDensityByMeganetColorTypeId(meganetColorId, colorTypes[0].meganetColorTypeId);
                                if (colorTypes.length > 0) {
                                  setFieldValue('meganetColorId', meganetColorId);
                                  setFieldValue('meganetColorTypeId', colorTypes[0].meganetColorTypeId);
                                  setFieldValue('meganetDensityId', densities[0].meganetDensityId);
                                }
                              }
                              else {
                                // No color is picked - set all values -1
                                setFieldValue('meganetColorId', meganetColorId);
                                setFieldValue('meganetColorTypeId', -1);
                                setFieldValue('meganetDensityId', -1);
                              }
                            }}
                          />
                        </FormControl>
                      </TableCell>
                      <TableCell align='left'>
                        <FormControl className={classes.formControl}>
                          <InputLabel id='colorType'>Color Type</InputLabel>
                          <ColorTypeSelectFormik
                            meganetColorId={values.meganetColorId}
                            meganetColorTypeId={values.meganetColorTypeId}
                            onChange={(meganetColorTypeId) => {
                              if (meganetColorTypeId !== -1) {
                                var densities = rxOrderContainer.getDensityByMeganetColorTypeId(values.meganetColorId, meganetColorTypeId);
                                if (densities.length > 0) {
                                  setFieldValue('meganetColorTypeId', meganetColorTypeId);
                                  setFieldValue('meganetDensityId', densities[0].meganetDensityId);
                                }
                              }
                              else {
                                // No color is picked - set all values -1
                                setFieldValue('meganetColorId', -1);
                                setFieldValue('meganetColorTypeId', meganetColorTypeId);
                                setFieldValue('meganetDensityId', -1);
                              }
                            }}
                          />
                        </FormControl>
                      </TableCell>
                      <TableCell align='left'>
                        <FormControl className={classes.formControl}>
                          <InputLabel id='denTop'>Density</InputLabel>
                          <DensitySelectFormik
                            meganetColorId={values.meganetColorId}
                            meganetColorTypeId={values.meganetColorTypeId}
                            meganetDensityId={values.meganetDensityId}
                            onChange={(meganetDensityId) => {
                              if (meganetDensityId !== -1) {
                                setFieldValue('meganetDensityId', meganetDensityId);
                              } else {
                                // No color is picked - set all values -1
                                setFieldValue('meganetColorId', -1);
                                setFieldValue('meganetColorTypeId', -1);
                                setFieldValue('meganetDensityId', meganetDensityId);
                              }
                            }}
                          />
                        </FormControl>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Chip size="small" style={{ color: 'black', backgroundColor: 'lightgray' }} label='Right' />
                      </TableCell>
                      <TableCell align='left'>
                        <FormControl className={classes.formControl}>
                          <InputLabel id='rightMeganetLensMasterId'>Lens</InputLabel>
                          <LensSelectFormik
                            meganetLensMasterId={values.rightMeganetLensMasterId}
                            name='rightMeganetLensMasterId'
                            onChange={rightMeganetLensMasterId => {
                              setFieldValue('rightMeganetLensMasterId', rightMeganetLensMasterId);

                              var defaultOptionIds: OptionId[] = [];

                              loadOptionsByMeganetLensMasterId(rightMeganetLensMasterId, 1);
                              var options = rxOrderContainer.getOptionsByRightLensMasterId(rightMeganetLensMasterId);
                              if (options) {
                                options.map(option => {
                                  defaultOptionIds.push({
                                    meganetOptionMasterId: option.meganetOptionMasterId,
                                    meganetOptionItemId: -1
                                  })
                                });
                              }

                              setFieldValue('rightOptionItemIds', defaultOptionIds);
                            }}
                          />
                        </FormControl>
                      </TableCell>
                      <TableCell align='right'><Field margin="normal" required fullWidth name='rightSph' label='Right Sph' type='number' id='rightSph' component={TextField} /></TableCell>
                      <TableCell align='right'><Field margin="normal" required fullWidth name='rightCyl' label='Right Cyl' type='number' id='rightCyl' component={TextField} /></TableCell>
                      <TableCell align='right'><Field margin="normal" required fullWidth name='rightAx' label='Right Ax' type='number' id='rightAx' component={TextField} /></TableCell>
                      <TableCell align='right'><Field margin="normal" fullWidth name='rightAdd' label='Right Add' type='number' id='rightAdd' component={TextField} /></TableCell>
                      <TableCell align='right'><Field margin="normal" fullWidth name='rightOutDia' label='Right OutDia' type='number' id='rightOutDia' component={TextField} /></TableCell>
                      {values.canEditRemarks || values.canEditRemarks === undefined ? 
                        <TableCell colSpan={2} align='right'> 
                          <Field validate={(value: string) => (!value.match(/^[ァ-ンヴーｧ-ﾝﾞﾟ\-0-9a-zA-Z]*$/) || (props.rowData.karteNo !== undefined ? value.length > 10 : value.length > 20)) ? `(カタカナ英数字、${props.rowData.karteNo !== undefined ? "10文字以内" : "20文字以内"})` : undefined} margin="normal" fullWidth name='remarks' label='Remarks' type='text' id='remarks' component={TextField} />
                        </TableCell> :
                        <TableCell colSpan={2} align='right'>
                          <FormLabel id='remarksLabel' style={{ fontSize: 13, }}>Remarks(編集不可)</FormLabel>
                          <br />
                          <FormLabel id='remarks' style={{ color: 'black' }}>{props.rowData.remarks}</FormLabel>
                        </TableCell>
                      }
                    </TableRow>
                    {isLoading ? <CircularProgress size={50} thickness={2} /> :
                      rxOrderContainer.getOptionsByRightLensMasterId(values.rightMeganetLensMasterId).length > 0 &&
                      <TableRow>
                        <TableCell>
                          <Chip size="small" style={{ color: 'black', backgroundColor: 'lightgray' }} label='Right Option' />
                        </TableCell>
                        <TableCell colSpan={7} key={values.rightMeganetLensMasterId} align='left'>
                          <Box
                            display="flex"
                            flexWrap="nowrap"
                            bgcolor="background.paper"
                            width="auto"
                          >
                            {rxOrderContainer.getOptionsByRightLensMasterId(values.rightMeganetLensMasterId).map((option: Option, index: number) => {
                                return (
                                  <React.Fragment key={index}>
                                    <Box mx={0.5}>
                                      <InputLabel style={{ fontSize: 11, }} id='option'>{option.optionName}</InputLabel>
                                      <RightOptionSelectFormik
                                        meganetOptionItemId={(() => {
                                          let meganetOptionItemId = -1;
                                          if (values.rightOptionItemIds) {
                                            // get option item id with option
                                            let optionId: any = values.rightOptionItemIds.find((id: OptionId) => id.meganetOptionMasterId === option.meganetOptionMasterId);
                                            meganetOptionItemId = optionId ? optionId.meganetOptionItemId : -1;
                                          }
                                          return meganetOptionItemId;
                                        })()}
                                        leftMeganetLensMasterId={values.leftMeganetLensMasterId}
                                        rightMeganetLensMasterId={values.rightMeganetLensMasterId}
                                        meganetOptionMasterId={option.meganetOptionMasterId}
                                        onChange={(meganetOptionItemId: number) => {
                                          if (values.rightOptionItemIds !== undefined) {
                                            var temp: OptionId[] = values.rightOptionItemIds.slice();
  
                                            if (meganetOptionItemId) {
                                              var exist = values.rightOptionItemIds.find((id: OptionId) => id.meganetOptionMasterId === option.meganetOptionMasterId)
  
                                              if (!exist) {
                                                temp.push({
                                                  meganetOptionMasterId: option.meganetOptionMasterId,
                                                  meganetOptionItemId: meganetOptionItemId
                                                });
                                              } else {
                                                temp.map(optionId => {
                                                  if (optionId.meganetOptionMasterId === option.meganetOptionMasterId) {
                                                    optionId.meganetOptionItemId = meganetOptionItemId;
                                                  }
                                                })
                                              }
                                            }
                                            setFieldValue('rightOptionItemIds', temp);
                                          }
                                        }}
                                        />
                                    </Box>
                                    {option.optionCode === '1221' && values.rightOptionItemIds?.some(optionItem => optionItem.meganetOptionMasterId === option.meganetOptionMasterId && optionItem.meganetOptionItemId !== -1) ?
                                      <Box mx={0.5}>
                                        <InputLabel style={{ fontSize: 11, }} id='option'>PD</InputLabel>
                                        <PdOptionText pd={values.rightPd} onChange={(rightPd: number) => {values.rightPd = rightPd}} /> 
                                      </Box>
                                      : <></>
                                    }
                                  </React.Fragment>
                                )
                              }
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    }
                    <TableRow >
                      <TableCell>
                        <Chip size="small" style={{ color: 'black', backgroundColor: 'lightgray' }} label='Left' />
                      </TableCell>
                      <TableCell align='left'>
                        <FormControl className={classes.formControl}>
                          <InputLabel id='leftMeganetLensMasterId'>Lens</InputLabel>
                          <LensSelectFormik
                            meganetLensMasterId={values.leftMeganetLensMasterId}
                            name='leftMeganetLensMasterId'
                            onChange={leftMeganetLensMasterId => {
                              setFieldValue('leftMeganetLensMasterId', leftMeganetLensMasterId);

                              var defaultOptionIds: OptionId[] = [];
                              loadOptionsByMeganetLensMasterId(leftMeganetLensMasterId, 0);
                              var options = rxOrderContainer.getOptionsByLeftLensMasterId(leftMeganetLensMasterId);
                              if (options) {
                                options.map(option => {
                                  defaultOptionIds.push({
                                    meganetOptionMasterId: option.meganetOptionMasterId,
                                    meganetOptionItemId: -1
                                  })
                                });
                              }
                              setFieldValue('leftOptionItemIds', defaultOptionIds);
                            }}
                          />
                        </FormControl>
                      </TableCell>
                      <TableCell align='right'><Field margin="normal" required fullWidth name='leftSph' label='Left Sph' type='number' id='leftSph' component={TextField} /></TableCell>
                      <TableCell align='right'><Field margin="normal" required fullWidth name='leftCyl' label='Left Cyl' type='number' id='leftCyl' component={TextField} /></TableCell>
                      <TableCell align='right'><Field margin="normal" required fullWidth name='leftAx' label='Left Ax' type='number' id='leftAx' component={TextField} /></TableCell>
                      <TableCell align='right'><Field margin="normal" fullWidth name='leftAdd' label='Left Add' type='number' id='leftAdd' component={TextField} /></TableCell>
                      <TableCell align='right'><Field margin="normal" fullWidth name='leftOutDia' label='Left OutDia' type='number' id='leftOutDia' component={TextField} /></TableCell>
                      <TableCell align='right'>
                        {(props.rowData.status === RxOrderStatusEnum.Waiting || props.rowData.status === RxOrderStatusEnum.Error) ?
                          (rxOrderContainer.isEditing || rxOrderContainer.isDeleting) && rxOrderContainer.selectedOrderId === props.rowData.meganetLensRxOrderId ?
                            <ConfirmCancelButtons meganetLensRxOrderId={props.rowData.meganetLensRxOrderId} isSubmitting={isSubmitting} onCancel={handleCancel} />
                            :
                            <EditDeleteButtons meganetLensRxOrderId={props.rowData.meganetLensRxOrderId} onEdit={handleEdit} onDelete={handleDelete} />
                          :
                          <></>
                        }
                      </TableCell>
                    </TableRow>
                    {isLoading ? <CircularProgress size={50} thickness={2} /> :
                      rxOrderContainer.getOptionsByLeftLensMasterId(values.leftMeganetLensMasterId).length > 0 &&
                      <TableRow>
                        <TableCell>
                          <Chip size="small" style={{ color: 'black', backgroundColor: 'lightgray' }} label='Left Option' />
                        </TableCell>
                        <TableCell colSpan={7} key={values.rightMeganetLensMasterId} align='left'>
                          <Box
                            display="flex"
                            flexWrap="nowrap"
                            bgcolor="background.paper"
                            width="auto"
                          >
                            {rxOrderContainer.getOptionsByLeftLensMasterId(values.leftMeganetLensMasterId).map((option: Option, index: number) => {
                                return (
                                  <React.Fragment key={index}>
                                    <Box mx={0.5}>
                                      <InputLabel style={{ fontSize: 11, }} id='option'>{option.optionName}</InputLabel>
                                      <LeftOptionSelectFormik
                                        meganetOptionItemId={(() => {
                                          let meganetOptionItemId = -1;
                                          if (values.leftOptionItemIds) {
                                            // get option item id with option
                                            let optionId: any = values.leftOptionItemIds.find((id: OptionId) => id.meganetOptionMasterId === option.meganetOptionMasterId);
                                            meganetOptionItemId = optionId ? optionId.meganetOptionItemId : -1;
                                          }
                                          return meganetOptionItemId;
                                        })()}
                                        leftMeganetLensMasterId={values.leftMeganetLensMasterId}
                                        rightMeganetLensMasterId={values.rightMeganetLensMasterId}
                                        meganetOptionMasterId={option.meganetOptionMasterId}
                                        onChange={(meganetOptionItemId: number) => {
                                          if (values.leftOptionItemIds !== undefined) {
                                            var temp: OptionId[] = values.leftOptionItemIds.slice();

                                            if (meganetOptionItemId) {
                                              var exist = values.leftOptionItemIds.find((id: OptionId) => id.meganetOptionMasterId === option.meganetOptionMasterId)

                                              if (!exist) {
                                                temp.push({
                                                  meganetOptionMasterId: option.meganetOptionMasterId,
                                                  meganetOptionItemId: meganetOptionItemId
                                                });
                                              } else {
                                                temp.map(optionId => {
                                                  if (optionId.meganetOptionMasterId === option.meganetOptionMasterId) {
                                                    optionId.meganetOptionItemId = meganetOptionItemId;
                                                  }
                                                })
                                              }
                                            }
                                            setFieldValue('leftOptionItemIds', temp);
                                          }
                                        }}
                                      />
                                    </Box>
                                    {option.optionCode === '1221' && values.leftOptionItemIds?.some(optionItem => optionItem.meganetOptionMasterId === option.meganetOptionMasterId && optionItem.meganetOptionItemId !== -1) ?
                                      <Box mx={0.5}>
                                        <InputLabel style={{ fontSize: 11, }} id='option'>PD</InputLabel>
                                        <PdOptionText pd={values.leftPd} onChange={(leftPd: number) => {values.leftPd = leftPd}} /> 
                                      </Box>
                                      : <></>
                                    }
                                  </React.Fragment>
                                )
                              }
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    }
                  </TableBody>
                </Table>
              </Form>
            )}
          </Formik>
        </TableCell>
      </TableRow >
    </>
  )
}) as React.FC<Props>